<template>
    <div>
        <!-- START:// Remember Me Dropdown -->
        <div v-if="NickNameMode === true">
            <div class="Input__Date TextField u-marginTop0">
                <select :aria-label="$t('loginpage.nickname')" class="Select" id="username" name="username" v-model="username">
                    <option v-for="name in UserNames" v-bind:key="name.Value" v-bind:value="name.Value">
                        {{ name.Text }}
                    </option>
                </select>
                <i class="Icon Icon__profile"></i>
            </div>
            <div class="u-flex u-marginTop0">
                <div class="u-inlineBlock">
                    <a tabindex="0" id="removeNickName" style="cursor:pointer;" @click="removeNickName" @keyup.enter="removeNickName">{{ $t('loginpage.RemoveNickname') }}</a>
                    <span> | </span>&thinsp;
                </div>
                <div class="u-inlineBlock u-marginTop0">
                    <a tabindex="0" id="removeNickName" style="cursor:pointer;" @click="differentUser" @keyup.enter="differentUser">{{ $t('loginpage.LoginAsDifferentUser') }}</a>
                </div>
            </div>
        </div>
        <!-- END:// Remember Me Dropdown -->
        <!-- START:// Username textbox -->
        <div v-else class="TextField">
            <span class="TextField__ID">
                <i class="Icon Icon__username"></i>
            </span>
            <label class="u-visuallyhidden" for="username">{{ $t('loginpage.username') }}</label>
            <input class="Input" id="username" v-bind:placeholder="$t('loginpage.username')" type="text" v-model="username" v-on:keyup.enter="trigger" />
        </div>
        <!-- END:// Username textbox -->
        <div class="TextField">
            <span class="TextField__ID">
                <i class="Icon Icon__login"></i>
            </span>
            <label class="u-visuallyhidden" for="password">{{ $t('loginpage.password') }}</label>
            <input class="Input" id="password" :placeholder="$t('loginpage.password')" :type="passwordFieldType" v-model="password" @keyup.enter="trigger" autocomplete="off" />
            <button class="Button__Link u-marginTop0" type="button" @click.prevent="showPassword" :aria-label="$t('loginpage.showPasswordButton')">
                <i class="Icon Icon__password-reveal"></i>
            </button>
        </div>
        <div class="Grid Grid--skinny u-marginTop1">
            <div v-if="NickNameMode === false" class="Column">
                <div class="Checkbox">
                    <input id="remember" type="checkbox" v-model="remember" :true-value="true" :false-value="false" />
                    <label for="remember" id="lblRemember">
                        {{ $t('loginpage.RememberMe') }}
                    </label>
                    <tippy-ext aria-live="assertive" :tippyTitle="$t('loginpage.tooltip.rememberme.title')" :tippyText="$t('loginpage.tooltip.rememberme.body')"></tippy-ext>
                </div>
            </div>
            <div class="Column u-textRight">
                <a href="#" @click.prevent="changeTab">
                    {{ $t('loginpage.ForgotPassword') }}
                    <span class="PopUp">
                        {{$t('popup.body')}}
                    </span>
                </a>
            </div>
        </div>
        <div v-if="remember === true" class="TextField">
            <span class="TextField__ID" id="iconspan">
                <i class="Icon Icon__profile" id="icon_prof"></i>
            </span>
            <label id="labelnickname" class="u-visuallyhidden" for="username">{{ $t('loginpage.nickname') }}</label>
            <label class="u-visuallyhidden" for="NickName"></label>
            <input class="Input" id="NickName" :placeholder="$t('loginpage.nickname')" type="text" v-model="NickName" />
        </div>
        <div class="u-marginTop2 u-xs-marginTop1" aria-live="assertive">
            <transition name="fade">
                <span class="Validation__Error" v-html="ValidationMessage" v-if="!IsValidData"></span>
            </transition>
        </div>
        <div class="u-marginTop3 u-xs-marginTop1">
            <button class="Button Button--fullWidth" type="submit" :disabled="v$.$invalid" ref="loginBtn" @click.prevent="login"><span class="span-wrapper">{{ $t('loginpage.LogIn') }}</span></button>
        </div>

        <forgot-password v-if="modal" @close-modal="modal = false"></forgot-password>
        <FullLoader :isLoading="isLoading" :label="label" :overlay="overlay"/>
    </div>
</template>

<style>
    .PopUp {
        height: 1px;
        overflow: hidden;
        position: absolute !important;
        width: 1px !important;
    }
</style>

<script>
    // Importing Packages
    import { createApp, ref, watch, onMounted, nextTick, computed } from 'vue'
    import { useVuelidate } from '@vuelidate/core'
    import { required, minLength } from '@vuelidate/validators'
    import axios from 'axios';
    import qs from 'qs';

    // Importing Components
    import FullLoader from './FullLoader.vue'
    import TippyExt from './TippyExt.vue'
    import ForgotPassword from './ForgotPassword.vue'
    import VueProgressBar from "@aacassandra/vue3-progressbar";

    export default {
        // Declaring components
        components: {
            ForgotPassword,
            FullLoader,
            TippyExt,
            VueProgressBar
        },
        // Declaring props
        props: {
            username: {
                default: ''
            }
        },
        emits: ['loginCallback'],
        // Setup function
        setup(props, { emit }) {
            // -- Data --
            // Declaring and initializing reactive properties
            const NickNameMode = ref(typeof nickNameMode !== 'undefined' ? nickNameMode : '');
            const remember = ref(false);
            const IsValidData = ref(true);
            const ValidationMessage = ref('');
            const IsLanguageCheck = ref(true);
            const IsValidBrowser = ref(true)
            const ShowForgotPassword = ref(false);
            const currentView = ref('ForgotPassword');
            const username = ref(typeof userNameFromModel !== 'undefined' ? userNameFromModel : '');
            const password = ref('');
            const label = ref('');
            const overlay = ref(true);
            const UserNames = ref(typeof userNameList !== 'undefined' ? userNameList : '');
            const NickName = ref('');
            const ReturnURL = ref(typeof returnURL !== 'undefined' ? returnURL : '');
            const IsToolTipVisible = ref(false);
            const passwordFieldType = ref('password');
            const loginBtn = ref(null);
            const isLoading = ref(false);
            const modal = ref(false);

            // -- VALIDATIONS --
            // Defining validation rules
            const rules = computed(() => ({
                username: { required },
                password: { required }
            }))

            // Using Vuelidate for form validation
            const v$ = useVuelidate(rules, { username, password });

            // Watching for changes in username
            watch(username, (newValue) => {
                username.value = (typeof newValue !== 'undefined' && newValue.length > 0) ? newValue : ''
            }, { deep: true });

            // On mounted lifecycle hook
            onMounted(async () => {
                await nextTick()
                if (NickNameMode.value === true) {
                    username.value = UserNames.value[0].Value
                }
            })

            // Function to change tab
            const changeTab = () => {
                modal.value = true;
            }

            // Function to remove nickname
            const removeNickName = () => {
                window.location = "/Account/RemoveNickName?NickName=" + username.value
            }

            // Function to switch user
            const differentUser = () => {
                window.location = "/Account/LogOn?ChangeUser=true&ReturnUrl=" + ReturnURL.value
            }

            // Function to toggle password visibility
            const showPassword = () => {
                passwordFieldType.value = (passwordFieldType.value === 'password') ? 'text' : 'password';
            }

            // Function to trigger login button click
            const trigger = () => {
                loginBtn.value.click();
            }

            // Function to show tooltip
            const showToolTip = () => {
                IsToolTipVisible.value = true
            }

            // Function to hide tooltip
            const hideToolTip = (e) => {
                IsToolTipVisible.value = false
            }

           
            // Function to handle login
            const login = () => {
                try {
                    IsValidData.value = true;
                    ValidationMessage.value = '';

                    var url = '/Account/LogOn/';

                    let token = document.getElementsByName('__RequestVerificationToken')[0].value;

                    var model = {
                        UserName: username.value,
                        Password: encodeURIComponent(password.value),
                        RememberMe: remember.value,
                        NickName: NickName.value,
                        isChangeUserName: isChangeUserName
                    }

                    var thetoken = "__RequestVerificationToken=" + token + '&';

                    var str = qs.stringify(model);
                    
                    isLoading.value = true;

                    // Making a POST request to '/Account/LogOn/'
                    axios.post(url, thetoken + str)
                        .then((response) => {
                            //window.location = '/';
                            if (response.data.IsValidData) {
                                // Call refreshTokens before proceeding
                                refreshTokens(response.data.ReturnPage).then(() => {
                                    //Tell the parent component that a login has occurred.
                                    //The true value could be used to indicate that the login was successful.
                                    emit('loginCallback', true);
                                    //window.location = response.data.ReturnPage;
                                }).catch((error) => {
                                    //console.error('Error with the login component:', error);
                                });
                            }
                            else {
                                IsValidData.value = response.data.IsValidData;
                                ValidationMessage.value = response.data.ValidationMessage;
                                IsValidBrowser.value = response.data.IsValidBrowser;
                                IsLanguageCheck.value = response.data.IsLanguageCheck;
                                ShowForgotPassword.value = response.data.ShowForgotPassword;
                                isLoading.value = false;
                            }
                        })
                        .catch((error) => {
                            isLoading.value = false;
                        });
                } catch (ex) {
                    isLoading.value = false;
                }
            }

            const refreshTokens = (path) => {
                return new Promise((resolve) => {
                    // Simulate an asynchronous operation (e.g., refreshing tokens) with a timeout
                    setTimeout(() => {
                        // Call resolve() to indicate that the Promise has been fulfilled
                        // This will trigger any .then() handlers attached to this Promise
                        resolve();
                        // Redirect the user to the home page after the timeout
                        window.location = path;
                    }, 1000); // Delay for 1000 milliseconds (1 second)
                });
            }

            // Returning reactive properties and methods
            return {
                v$,
                label,
                removeNickName,
                differentUser,
                showPassword,
                trigger,
                showToolTip,
                hideToolTip,
                login,
                NickNameMode,
                remember,
                IsValidData,
                ValidationMessage,
                IsLanguageCheck,
                IsValidBrowser,
                ShowForgotPassword,
                currentView,
                username,
                password,
                overlay,
                UserNames,
                NickName,
                ReturnURL,
                IsToolTipVisible,
                passwordFieldType,
                loginBtn,
                isLoading,
                changeTab,
                modal,
                refreshTokens
            }
        }
    }

</script>

